import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import '@/styles/charts.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  TooltipProps
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface UserActivityData {
  role: string;
  active: number;
  inactive: number;
}

const data: UserActivityData[] = [
  { role: 'Property Owners', active: 45, inactive: 5 },
  { role: 'FlexHelpers', active: 32, inactive: 3 },
  { role: 'Managers', active: 18, inactive: 2 },
  { role: 'Admins', active: 8, inactive: 1 },
];

// Custom color palette
const colors = {
  active: 'hsl(142 72% 29%)',     // Emerald green
  inactive: 'hsl(220 9% 87%)'     // Light gray
};

const CustomXAxis = (props: any) => (
  <XAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

const CustomYAxis = (props: any) => (
  <YAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

export function UserActivity() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>User Activity by Role</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <CustomXAxis dataKey="role" />
              <CustomYAxis />
              <Tooltip
                content={({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
                  if (active && payload?.length) {
                    // Convert all values to numbers before summing
                    const total = payload.reduce((sum, entry) => {
                      // Handle different value types safely
                      const numValue = typeof entry.value === 'number' 
                        ? entry.value 
                        : (Array.isArray(entry.value) ? 0 : Number(entry.value) || 0);
                      return sum + numValue;
                    }, 0);

                    // Find active users entry and convert to number safely
                    const activeEntry = payload.find(p => p.name === 'Active Users');
                    const activeUsers = typeof activeEntry?.value === 'number'
                      ? activeEntry.value
                      : (Array.isArray(activeEntry?.value) ? 0 : Number(activeEntry?.value) || 0);
                    
                    // Calculate percentage
                    const activePercentage = total > 0 ? ((activeUsers / total) * 100).toFixed(1) : '0.0';

                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-sm">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="mt-1 space-y-1">
                          {payload.map((entry) => (
                            <div
                              key={entry.name}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className={`chart-color-indicator ${entry.dataKey === 'active' ? 'color-active' : 'color-inactive'}`}
                              />
                              <span className="text-muted-foreground">
                                {entry.name}: {entry.value} users
                              </span>
                            </div>
                          ))}
                          <div className="pt-1 border-t">
                            <div className="text-sm text-muted-foreground">
                              Total: {total} users
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Active Rate: {activePercentage}%
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Bar
                dataKey="active"
                name="Active Users"
                stackId="a"
                fill={colors.active}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="inactive"
                name="Inactive Users"
                stackId="a"
                fill={colors.inactive}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}